import React, { useState } from 'react';
import { Box, Typography, Card, Container, Button, Input, Textarea, Chip, LinearProgress } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import axios from 'axios';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const projectTypes = [
  'Web Application', 'Mobile App', 'AI/ML System', 'E-commerce Platform',
  'Data Analytics', 'IoT Solution', 'API Service', 'Enterprise Software'
];

const techSuggestions = [
  'React', 'Next.js', 'FastAPI', 'Python', 'Go', 'TypeScript',
  'AI/ML', 'Cloud Native', 'Real-time', 'Mobile-first'
];

export const ProjectGeneratorSection = () => {
  const [formData, setFormData] = useState({
    email: '',
    projectType: '',
    description: '',
    selectedTech: [],
    timeline: 'ASAP'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTechToggle = (tech) => {
    setFormData(prev => ({
      ...prev,
      selectedTech: prev.selectedTech.includes(tech)
        ? prev.selectedTech.filter(t => t !== tech)
        : [...prev.selectedTech, tech]
    }));
  };

  const submitProjectRequest = async () => {
    if (!formData.description.trim()) {
      toast.error('Please describe your project idea');
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error('Please provide your email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        '/api/analyze-project',
        {
          description: formData.description,
          projectType: formData.projectType,
          technologies: formData.selectedTech,
          timeline: formData.timeline,
          email: formData.email,
          name: formData.email // Use email as name for now
        }
      );
      
      setIsSubmitting(false);
      toast.success(response.data.message);
      
      // Clear form after successful submission
      setFormData({ 
        email: '', 
        projectType: '', 
        description: '', 
        selectedTech: [], 
        timeline: 'ASAP' 
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setIsSubmitting(false);
      
      // Handle different error types
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'] || 60;
        toast.error(`Rate limit exceeded. Please wait ${retryAfter} seconds before trying again.`);
      } else if (error.response?.status === 413) {
        toast.error('Project description is too long. Please shorten it and try again.');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Invalid request. Please check your input.');
      } else {
        toast.error('Something went wrong. Please try again or contact us directly.');
      }
    }
  };


  return (
    <Box id="assessment" sx={{ py: 12, background: 'rgba(0, 0, 0, 0.8)' }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              level="h2" 
              sx={{ 
                mb: 2,
                background: 'linear-gradient(135deg, #0B4BB3 0%, #00D4FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Smart Project Assessment
            </Typography>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Describe your business challenge and get instant AI-powered analysis of solutions, timelines, and ROI potential
            </Typography>
          </Box>
        </motion.div>

        <Card
          sx={{
            p: 6,
            background: 'rgba(2, 6, 23, 0.7)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(100, 116, 139, 0.3)',
            borderRadius: 3,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 6 }}>
            
            {/* Left side - Form */}
            <Box>
              <Typography level="h3" sx={{ mb: 4, color: 'text.primary' }}>
                Tell us about your business challenge
              </Typography>

              {/* Email */}
              <Box sx={{ mb: 3 }}>
                <Typography level="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Email
                </Typography>
                <Input
                  placeholder="your.email@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  sx={{ width: '100%' }}
                />
              </Box>

              {/* Project Type */}
              <Box sx={{ mb: 3 }}>
                <Typography level="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Project Type
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {projectTypes.map((type) => (
                    <Chip
                      key={type}
                      variant={formData.projectType === type ? 'solid' : 'outlined'}
                      color="primary"
                      onClick={() => setFormData(prev => ({ ...prev, projectType: type }))}
                      sx={{ cursor: 'pointer' }}
                    >
                      {type}
                    </Chip>
                  ))}
                </Box>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography level="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Project Description
                </Typography>
                <Textarea
                  placeholder="Describe your project idea, goals, and any specific requirements..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  minRows={4}
                  sx={{ width: '100%' }}
                />
              </Box>

              {/* Tech Preferences */}
              <Box sx={{ mb: 4 }}>
                <Typography level="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Preferred Technologies (optional)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {techSuggestions.map((tech) => (
                    <Chip
                      key={tech}
                      variant={formData.selectedTech.includes(tech) ? 'solid' : 'outlined'}
                      color="primary"
                      onClick={() => handleTechToggle(tech)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {tech}
                    </Chip>
                  ))}
                </Box>
              </Box>

              {/* Generate Analysis Button */}
              <Button
                variant="solid"
                color="primary"
                size="lg"
                onClick={submitProjectRequest}
                disabled={isSubmitting || !formData.description.trim() || !formData.email.trim()}
                startDecorator={<AutoAwesomeIcon />}
                sx={{ width: '100%', mb: 2 }}
              >
                {isSubmitting ? 'Submitting...' : 'Get Free AI Project Analysis'}
              </Button>

              {isSubmitting && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                  <Typography level="body2" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                    Analyzing your project and sending results to your email...
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right side - What You'll Receive */}
            <Box>
              <Typography level="h3" sx={{ mb: 4, color: 'text.primary' }}>
                What You'll Receive
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                <Card variant="soft" sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <AccessTimeIcon sx={{ fontSize: '1.5rem', color: '#00D4FF' }} />
                    <Typography level="h4" sx={{ color: 'text.primary' }}>
                      Project Timeline
                    </Typography>
                  </Box>
                  <Typography level="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Detailed development timeline with milestones and delivery phases
                  </Typography>
                </Card>

                <Card variant="soft" sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <AttachMoneyIcon sx={{ fontSize: '1.5rem', color: '#00FF88' }} />
                    <Typography level="h4" sx={{ color: 'text.primary' }}>
                      Cost Estimates
                    </Typography>
                  </Box>
                  <Typography level="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    Transparent pricing breakdown with different budget ranges and options
                  </Typography>
                </Card>

                <Card variant="soft" sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <AutoAwesomeIcon sx={{ fontSize: '1.5rem', color: '#2E5BBA' }} />
                    <Typography level="h4" sx={{ color: 'text.primary' }}>
                      Technology Recommendations
                    </Typography>
                  </Box>
                  <Typography level="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    AI-powered tech stack recommendations optimized for your specific project needs
                  </Typography>
                </Card>

                <Card 
                  sx={{ 
                    p: 4, 
                    background: 'linear-gradient(135deg, rgba(46, 91, 186, 0.1) 0%, rgba(0, 168, 107, 0.1) 100%)',
                    border: '1px solid rgba(46, 91, 186, 0.2)' 
                  }}
                >
                  <Typography level="body1" sx={{ color: 'text.primary', textAlign: 'center', fontWeight: 600 }}>
                    📧 Delivered directly to your email within minutes
                  </Typography>
                  <Typography level="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}>
                    Plus follow-up consultation from our expert team
                  </Typography>
                </Card>

              </Box>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};